import React, { useState, useRef, useEffect } from 'react';
import Countdown from 'react-countdown';
import ExamviewButton from '../../components/common/form/ExamviewButton.js';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import NotStartedIcon from '@mui/icons-material/NotStarted';
import { setExamPausedTimerShow } from './PauseManage.js';
import { use } from 'react';

const PAUSE_TIME_STORAGE_KEY = 'examPauseTimeRemaining';
const MAIN_TIMER_STORAGE_KEY = 'examMainTimeRemaining';
const PAUSE_STATE_STORAGE_KEY = 'examPauseState';
const PAUSE_END_TIME_STORAGE_KEY = 'examPauseEndTime';

const Completionlist = () => (
  <span>Time exceeded! Submit your work within next 10 seconds!</span>
);

const padZero = (num) => (num < 10 ? '0' : '');

const renderer = ({ days, hours, minutes, seconds, completed }) => {
  if (completed) return <Completionlist />;

  const spancolor =
    hours < 1 && minutes < 10 && days < 1
      ? 'label label-lg label-light-danger label-inline font-size-h1 time-count-text'
      : 'label label-lg label-light-success label-inline time-remaining time-count-text';

  const timeString =
    days > 0
      ? `${padZero(days)}${days}d : ${padZero(hours)}${hours}h : ${padZero(minutes)}${minutes}m : ${padZero(seconds)}${seconds}s`
      : `${padZero(hours)}${hours}h : ${padZero(minutes)}${minutes}m : ${padZero(seconds)}${seconds}s`;

  return (
    <div className="row" style={{ textAlign: 'start', alignItems: 'center' }}>
      <span className="font-size-md position-center text-muted text-center bottom-text time-remaining-text">
        Time Left &emsp;
      </span>
      <span className={spancolor}>{timeString}</span>
      <br />
    </div>
  );
};

export function ExamTimer(props) {


 const getInitialPauseTime = () => {
  const stored = localStorage.getItem(PAUSE_TIME_STORAGE_KEY);
      const parsed = parseInt(stored, 10);
    if (stored && !isNaN(parsed) && parsed > 0) {
      return !isNaN(parsed) ? parsed : 0;
    } else {
      const p = Number(props.pause_until) || 0;
      if (p > 0 && p < 1000) {
        // treat as minutes
        return p * 60000;
      }
      return p;
    }
  };


  const countdownRef = useRef(null);
  const pauseCountdownRef = useRef(null);
  const pauseButtonRef = useRef(null);

  const [showPauseTimer, setShowPauseTimer] = useState(false);
  const [remainingPauseTime, setRemainingPauseTime] = useState(0);
  

  const [isPaused, setIsPaused] = useState(() => {
    try {
      const savedPauseState = localStorage.getItem(PAUSE_STATE_STORAGE_KEY);
      return savedPauseState === 'true';
    } catch (e) {
      return false;
    }
  });
  const [pauseEndTime, setPauseEndTime] = useState(() => {
    try {
      const savedEndTime = localStorage.getItem(PAUSE_END_TIME_STORAGE_KEY);
      const parsed = parseInt(savedEndTime, 10);
      return !isNaN(parsed) && parsed > Date.now() ? parsed : null;
    } catch (e) {
      return null;
    }
  });

  const [mainEndTime, setMainEndTime] = useState(() => {
    try {
      const storedMainTime = localStorage.getItem(MAIN_TIMER_STORAGE_KEY);
      const parsed = parseInt(storedMainTime, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return Date.now() + parsed+1000;
      }
    } catch (e) { }

    return Date.now() + (Number(props.time_int) || 0);
  });

  

// Add effect to save pause state changes
  useEffect(() => {
    try {
      localStorage.setItem(PAUSE_STATE_STORAGE_KEY, isPaused);
      if (pauseEndTime) {
        localStorage.setItem(PAUSE_END_TIME_STORAGE_KEY, String(pauseEndTime));
      } else {
        localStorage.removeItem(PAUSE_END_TIME_STORAGE_KEY);
      }
    } catch (e) {}
  }, [isPaused, pauseEndTime]);

  // on mount, if already paused, show pause timer
  useEffect(() => {
    if (isPaused && pauseEndTime) {
      setShowPauseTimer(true);
      setExamPausedTimerShow(true);
      document.body.style.backgroundColor = 'rgba(0, 123, 255, 0.08)';
      
      if (countdownRef.current?.pause) {
        countdownRef.current.pause();
      }
    }
  }, []);

    // set remainingPauseTime whenever props.pause_until changes (or on mount)
  useEffect(() => {
    const val = getInitialPauseTime();
    setRemainingPauseTime(val);
  },[props.pause_until]);
 
  // store remaining pause time on change
  useEffect(() => {
    try {
      localStorage.setItem(PAUSE_TIME_STORAGE_KEY, String(remainingPauseTime));
    } catch (e) {}
  }, [remainingPauseTime]);

  // store remaining main time when paused or on unmount (optional)
  useEffect(() => {
    const saveRemaining = () => {
      try {
        const remainingMainTime = Math.max(0, mainEndTime - Date.now());
        localStorage.setItem(MAIN_TIMER_STORAGE_KEY, String(remainingMainTime));
      } catch (e) {}
    };

    if (isPaused) saveRemaining();
    const onBeforeUnload = () => saveRemaining();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [isPaused, mainEndTime]);


  // focus button when pause UI opens
  useEffect(() => {
    if (showPauseTimer && pauseButtonRef.current && typeof pauseButtonRef.current.focus === 'function') {
      try { pauseButtonRef.current.focus(); } catch (e) {}
    }
  }, [showPauseTimer]);


  // update remainingPauseTime while pause modal shown
  useEffect(() => {
    if (!showPauseTimer || !pauseEndTime) return undefined;
    const tick = () => {
      const msLeft = Math.max(0, pauseEndTime - Date.now());
      setRemainingPauseTime(msLeft);
      if (msLeft <= 0) {
        setIsPaused(false);
        setShowPauseTimer(false);
        setExamPausedTimerShow(false);
      }
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [showPauseTimer, pauseEndTime]);

  const handlePauseResume = () => {
    if (isPaused) {
    // RESUME
   try {
      const storedMainTime = localStorage.getItem(MAIN_TIMER_STORAGE_KEY);
      const remainingMainTime = parseInt(storedMainTime, 10);
      
      if (!isNaN(remainingMainTime) && remainingMainTime > 0) {
        setMainEndTime(Date.now() + remainingMainTime);
        
        localStorage.removeItem(MAIN_TIMER_STORAGE_KEY);
      }

      const now = Date.now();
      const remainingMs = pauseEndTime ? Math.max(0, pauseEndTime - now) : remainingPauseTime;
      setRemainingPauseTime(remainingMs);
      setIsPaused(false);
      setShowPauseTimer(false);
      setExamPausedTimerShow(false);

      if (countdownRef.current?.start) {
        countdownRef.current.start();
      }
      
      document.body.style.backgroundColor = '';
    } catch (e) {
      console.warn('Error resuming timer:', e);
    }
  } else {
      // PAUSE
      
      try { 
        const remainingMainTime = Math.max(0, mainEndTime - Date.now());
        localStorage.setItem(MAIN_TIMER_STORAGE_KEY, String(remainingMainTime)); } catch (e) {}
      if (remainingPauseTime <= 0) {
        window.Swal.fire({ title: 'No Pause Time Left', text: 'You have used all your available pause time.', icon: 'warning', confirmButtonColor: '#3085d6' });
        return;
      }

      window.Swal.fire({
        title: 'Are you sure you want to pause?',
        html: `You have <b>${Math.floor(remainingPauseTime / 60000)}m ${Math.floor((remainingPauseTime % 60000) / 1000)}s</b> of pause time available. <br/> During this time, you will not be able to interact with the exam. <br/> <b>Click Resume to continue.</b>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, pause exam',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          setIsPaused(true);
          setExamPausedTimerShow(true);
          try {
            if (countdownRef.current && typeof countdownRef.current.pause === 'function') {
              countdownRef.current.pause();
            }
          } catch (e) {}
          const pauseUtl = Date.now() + remainingPauseTime;
          setPauseEndTime(pauseUtl);
          setShowPauseTimer(true);
          document.body.style.backgroundColor = 'rgba(0, 123, 255, 0.08)';
        }
      });
    }
  };

  const handlePauseComplete = () => {

    setIsPaused(false);
    setShowPauseTimer(false);
    setRemainingPauseTime(0);
    setExamPausedTimerShow(false);
    setPauseEndTime(null);
    
    try {
      localStorage.removeItem(PAUSE_STATE_STORAGE_KEY);
      localStorage.removeItem(PAUSE_END_TIME_STORAGE_KEY);
      
      if (countdownRef.current?.start) {
        countdownRef.current.start();
      }
    } catch (e) {}
    
    document.body.style.backgroundColor = '';
  };

    useEffect(() => {
    return () => {
      try {
        // Only remove storage if not paused
        if (!isPaused) {
          localStorage.removeItem(PAUSE_STATE_STORAGE_KEY);
          localStorage.removeItem(PAUSE_END_TIME_STORAGE_KEY);
          
        }
      } catch (e) {}
    };
  }, [isPaused]);

  return (
    <div className="time-remaining-wrap" style={{ position: 'relative' }}>
      <Countdown ref={countdownRef} date={mainEndTime} renderer={renderer} />

      {showPauseTimer && (
        <div
          className="interaction-blocker"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.4)', zIndex: 900, pointerEvents: 'auto' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        />
      )}

      {showPauseTimer && pauseEndTime && (
        <div className="pause-timer-container" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', zIndex: 1000, background: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 0 15px rgba(0,0,0,0.4)' }} onClick={(e) => e.stopPropagation()}>
          <h2 className="mb-4">EXAM PAUSED</h2>
          <div style={{ fontSize: '2em' }}>
            <Countdown ref={pauseCountdownRef} date={pauseEndTime} renderer={renderer} onComplete={handlePauseComplete} />
          </div>
        </div>
      )}

      {props.is_pause && (
        <div className="d-flex align-items-center" style={{ position: 'relative', zIndex: 1100 }}>
          <ExamviewButton
            ref={pauseButtonRef}
            className="btn btn-info font-weight-bolder pl-2 pr-1 py-2 mr-3 mb-2 mt-2"
            type="button"
            onClick={handlePauseResume}
            btnName={isPaused ? <PauseCircleIcon /> : <NotStartedIcon />}
            disabled={!props.is_pause || remainingPauseTime <= 0}
            style={{ pointerEvents: 'auto', zIndex: 1000, position: 'relative', backgroundColor: showPauseTimer ? '#007bff' : undefined, color: showPauseTimer ? '#fff' : undefined }}
          />

          {!isPaused && (
            <div className="pause-time-display" style={{ padding: '5px 10px', borderRadius: '4px', backgroundColor: '#e9ecef', fontSize: '0.9rem', color: '#495057', pointerEvents: 'none', opacity: showPauseTimer ? 0.45 : 1, transition: 'opacity 150ms ease' }}>
              Pause Time Left: {Math.floor(remainingPauseTime / 60000)}m {Math.floor((remainingPauseTime % 60000) / 1000)}s
            </div>
          )}
        </div>
      )}
    </div>
  );
}
